/**
 * ScorePill — coloured score badge for a W/D/L outcome.
 *
 * Two inline implementations previously lived in `IhsLast5Games`
 * (hex-colour set, draw variant) and `IhsLast5GamesCompact` (CSS-var
 * set, missing draw variant — bug). This primitive collapses both: the
 * canonical colour source is CSS vars (`--status-positive` /
 * `--status-negative` / `--status-neutral`) and the draw variant is
 * always present.
 *
 * Two size presets. `md` matches the `.scoreBox` in `IhsLast5Games`,
 * `sm` matches the compact `.scorePill` for video-overlay rows.
 *
 * Shape: { scoreLine: string; outcome: 'W'|'D'|'L'; size?: 'sm'|'md' }
 */

import type { ReactElement } from 'react';
import { z } from 'zod';
import type { PrimitiveMeta } from './_meta.js';
import styles from './ScorePill.module.css';

const dataShape = z.object({
  /** Render-ready score string (e.g. "2-1"). Primitive does not format. */
  scoreLine: z.string(),
  outcome: z.enum(['W', 'D', 'L']),
  size: z.enum(['sm', 'md']).optional(),
});

export const META: PrimitiveMeta = {
  name: 'score-pill',
  description: 'Coloured W/D/L pill carrying a "home-away" score string.',
  composable: true,
  dataShape,
  densities: ['compact'],
  breakpoints: { mobile: true, wide: true },
  animation: 'none',
};

interface ScorePillProps {
  /** Render-ready score string (e.g. "2-1"). The primitive does not format. */
  scoreLine: string;
  outcome: 'W' | 'D' | 'L';
  size?: 'sm' | 'md';
}

export function ScorePill({
  scoreLine,
  outcome,
  size = 'md',
}: ScorePillProps): ReactElement {
  const sizeClass = size === 'sm' ? styles.sm : styles.md;
  const outcomeClass =
    outcome === 'W'
      ? styles.win
      : outcome === 'L'
        ? styles.loss
        : styles.draw;

  return (
    <span className={`${styles.pill} ${sizeClass} ${outcomeClass}`}>
      {scoreLine}
    </span>
  );
}

export default ScorePill;
